import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import { Storage } from 'aws-amplify';
import { download } from 'utilities/file';
import { useDropzone } from 'react-dropzone';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useCache } from 'CacheProvider';
import { toastr } from 'react-redux-toastr';
import { request } from 'utilities/graph';
import { listInstructionDownloadFiles } from 'graphql/queries';
import { createInstructionDownloadFile } from 'graphql/mutations';
import cache from 'utilities/cache';

const useStyles = makeStyles((theme) => ({
  mediaContainer: {
    position: 'relative',
  },
  media: {
    height: '70%',
    opacity: 0.1,
  },
  fileContainer: {
    position: 'absolute',
    top: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
  },
  card: {
    height: '100%',
  },
  loadingContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  root: {
    position: 'relative',
    width: '100%',
  },
  title: {
    fontSize: '2.2rem',
    fontWeight: 'bold',
    marginBottom: '20px',
    color: theme.palette.text.primary,
  },
  cardContent: {
    position: 'relative',
  },
  description: {
    marginBottom: 8,
  },
  updatedAt: {
    fontSize: '0.875rem',
    color: '#666',
    marginBottom: 8,
  },
  uploadSection: {
    'marginBottom': theme.spacing(3),
    'padding': theme.spacing(2),
    'border': '2px dashed #ccc',
    'borderRadius': theme.spacing(1),
    'textAlign': 'center',
    'cursor': 'pointer',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  progress: {
    marginTop: theme.spacing(2),
  },
}));

const getRelativeTimeString = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  if (diffInSeconds < 60) return '剛剛';
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes} 分鐘前`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours} 小時前`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `${diffInDays} 天前`;
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) return `${diffInMonths} 個月前`;
  const diffInYears = Math.floor(diffInMonths / 12);
  return `${diffInYears} 年前`;
};

export default function FileDownloadGrids({ category }) {
  const classes = useStyles();
  const { appGroup } = useCache();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const isAdmin = appGroup === 'Admins';

  const loadFilesFromAPI = async () => {
    try {
      setIsLoading(true);

      const { data: { listInstructionDownloadFiles: result } } = await request(listInstructionDownloadFiles, {
        filter: category ? { category: { eq: category } } : null,
      });

      const documentFiles = result.items.map((item) => ({
        id: item.id,
        title: item.title,
        description: item.description,
        fileS3Key: item.fileS3Key,
        thumbnailUrl: item.thumbnailUrl || '/images/default-excel.png',
        updatedAt: item.updatedAt,
        category: item.category,
      }));

      setFiles(documentFiles);
    } catch (error) {
      console.error('Error loading files from API:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshFileList = () => {
    loadFilesFromAPI();
  };

  useEffect(() => {
    loadFilesFromAPI();
  }, [category]);

  const handleDownload = async (fileS3Key, title) => {
    try {
      if (fileS3Key) {
        const url = await Storage.get(fileS3Key);
        const fileResponse = await fetch(url);
        if (!fileResponse.ok) {
          throw new Error('下載檔案失敗');
        }
        const blob = await fileResponse.blob();
        const fileName = fileS3Key.split('/').pop();
        download(fileName, blob);
        toastr.success('檔案下載成功');
      } else {
        toastr.error('檔案下載失敗');
      }
    } catch (error) {
      console.error('Download error:', error);
      toastr.error('檔案下載失敗');
    }
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
  } = useDropzone({
    async onDrop(newFiles) {
      setIsUploading(true);

      try {
        for (const file of newFiles) {
          const s3Key = `documents/${file.name}`;

          await Storage.put(s3Key, file, {
            contentType: file.type,
          });

          const username = cache.get('app:username');
          const now = new Date().toISOString();
          const input = {
            title: file.name.replace(/\.[^/.]+$/, ''),
            description: `上傳於 ${new Date().toLocaleDateString()}`,
            fileS3Key: s3Key,
            thumbnailUrl: '/images/default-excel.png',
            category: category || '上傳檔案',
            createdAt: now,
            updatedAt: now,
            createdBy: username,
          };

          await request(createInstructionDownloadFile, { input });

          setUploadedFiles((prev) => [...prev, { name: file.name, s3Key }]);
          toastr.success(`檔案 ${file.name} 上傳成功！`);
        }

        refreshFileList();
      } catch (error) {
        console.error('Upload error:', error);
        toastr.error('檔案上傳失敗');
      } finally {
        setIsUploading(false);
      }
    },
    accept: '.xlsx,.xls,.pdf,.csv',
  });

  if (isLoading) {
    return (
      <div className={classes.root}>
        <Typography variant="h4" className={classes.title}>檔案下載</Typography>
        <Divider style={{ marginBottom: 20 }} />
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <CircularProgress />
          <Typography variant="body1" style={{ marginTop: 16 }}>
            載入檔案列表中...
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Typography variant="h4" className={classes.title}>檔案下載</Typography>
      <Divider style={{ marginBottom: 20 }} />

      {/* Admin專用的檔案上傳區域 */}
      {isAdmin && (
        <div style={{ marginBottom: 24 }}>
          <Typography variant="h6" gutterBottom>
            檔案上傳 (僅無敵模式管理員)
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            將檔案拖曳到此處或點擊選擇檔案
          </Typography>

          <div {...getRootProps()} className={classes.uploadSection}>
            <input {...getInputProps()} />
            {isDragActive ? (
              <Typography>將檔案拖曳到此處...</Typography>
            ) : (
              <Typography>拖曳檔案到此處，或點擊選擇檔案</Typography>
            )}

            {isUploading && (
              <div className={classes.progress}>
                <CircularProgress size={24} />
                <Typography variant="body2" style={{ marginTop: 8 }}>
                  上傳中...
                </Typography>
              </div>
            )}
          </div>

          {uploadedFiles.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Typography variant="subtitle1" gutterBottom>
                本次上傳的檔案：
              </Typography>
              {uploadedFiles.map((file, index) => (
                <Typography key={index} variant="body2">
                  ✓ {file.name} (S3 Key: {file.s3Key})
                </Typography>
              ))}
            </div>
          )}
        </div>
      )}

      <Grid container spacing={2}>
        {files.length === 0 && <Grid item xs={12}>{'尚無可下載檔案'}</Grid>}
        {files.map(({ id, title, description, fileS3Key, thumbnailUrl, tags, mediaType, updatedAt, category }) => (
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={id}>
            <Card className={classes.card}>
              <div
                className={classes.mediaContainer}
                onClick={() => handleDownload(fileS3Key, title)}
                style={{ cursor: 'pointer' }}
              >
                <CardMedia
                  className={classes.media}
                  image="/logos/client_logo_256.png"
                  title="縮圖"
                  component="img"
                  onError={(e) => {
                    if (e.target.src.includes('client_logo_256.png')) {
                      e.target.onerror = null;
                      return;
                    }
                    e.target.src = '/logos/client_logo_256.png';
                  }}
                />
                <div className={classes.fileContainer}>
                  <img
                    src="/images/default-excel.png"
                    alt="Excel"
                    style={{ width: 48, height: 48, objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              </div>
              <CardContent className={classes.cardContent}>
                <Typography gutterBottom variant="h5" component="h2">
                  {title}
                </Typography>
                <Typography variant="body2" color="textSecondary" component="p" className={classes.description}>
                  {description}
                </Typography>
                <Typography variant="body2" className={classes.updatedAt}>
                  更新於 {getRelativeTimeString(updatedAt)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );
}

FileDownloadGrids.propTypes = {
  category: PropTypes.string,
};
